import type { DropFacetRow, FiltersDropDateRange, OutPriceFacetRow, OutPriceRange } from '@/ApiClient';

/** One histogram bar: a distinct amount and how many eligible articles carry it. */
export type OutPriceAmountCount = { amount: number; count: number };

/** The slider selections that shape the counts (each histogram excludes its own). */
export type SliderSelections = {
  outPriceAmounts?: Record<string, number[]>;
  outPriceRanges?: Record<string, OutPriceRange>;
  dropDateRange?: FiltersDropDateRange | null;
};

/**
 * Article sets passing each price key's active selection, mirroring the backend's
 * `ProductArticleFilterService` semantics: the checkbox list and the range are separate
 * conditions (an article can satisfy them through different SKU values), matching on the
 * out-price key regardless of destination.
 */
export function pricePredicateSets(rows: OutPriceFacetRow[], selections: SliderSelections): Map<string, Set<number>> {
  const sets = new Map<string, Set<number>>();

  const collect = (key: string, matches: (amount: number) => boolean) => {
    const passing = new Set<number>();
    for (const row of rows) if (row.key === key && matches(row.amount)) passing.add(row.articleId);
    const existing = sets.get(key);
    sets.set(key, existing ? intersect(existing, passing) : passing);
  };

  for (const [key, amounts] of Object.entries(selections.outPriceAmounts ?? {})) {
    if (!amounts?.length) continue;
    const selected = new Set(amounts);
    collect(key, (amount) => selected.has(amount));
  }

  for (const [key, range] of Object.entries(selections.outPriceRanges ?? {})) {
    if (range?.min == null && range?.max == null) continue;
    collect(key, (amount) => (range.min == null || amount >= range.min) && (range.max == null || amount <= range.max));
  }

  return sets;
}

/**
 * Articles with any drop whose date falls in the selected range, or null when no range
 * is selected. `dropDates` maps drop id to its date in epoch ms.
 */
export function dropPredicateSet(
  dropRows: DropFacetRow[],
  range: FiltersDropDateRange | null | undefined,
  dropDates: Map<number, number>
): Set<number> | null {
  if (!range || (range.from == null && range.to == null)) return null;

  const from = range.from ? new Date(range.from).getTime() : -Infinity;
  const to = range.to ? new Date(range.to).getTime() : Infinity;

  const passing = new Set<number>();
  for (const row of dropRows) {
    const date = dropDates.get(row.dropId);
    if (date != null && date >= from && date <= to) passing.add(row.articleId);
  }
  return passing;
}

/** Intersection of the given sets; null entries mean "no restriction" and null overall means the same. */
export function intersectAll(sets: (Set<number> | null)[]): Set<number> | null {
  const real = sets.filter((s): s is Set<number> => s != null);
  if (real.length === 0) return null;
  return real.reduce(intersect);
}

function intersect(a: Set<number>, b: Set<number>): Set<number> {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  const result = new Set<number>();
  for (const id of small) if (large.has(id)) result.add(id);
  return result;
}

/**
 * Bars for one price histogram: every amount on the (unfiltered) axis with the number of
 * eligible articles carrying it. `eligible` is the intersection of every *other* slider's
 * predicate — null when nothing restricts.
 */
export function outPriceAmountCounts(
  rows: OutPriceFacetRow[],
  key: string,
  destinationId: number,
  allAmounts: number[],
  eligible: Set<number> | null
): OutPriceAmountCount[] {
  const articlesByAmount = new Map<number, Set<number>>();
  for (const row of rows) {
    if (row.key !== key || row.destinationId !== destinationId) continue;
    if (eligible && !eligible.has(row.articleId)) continue;
    let set = articlesByAmount.get(row.amount);
    if (!set) articlesByAmount.set(row.amount, (set = new Set()));
    set.add(row.articleId);
  }
  return allAmounts.map((amount) => ({ amount, count: articlesByAmount.get(amount)?.size ?? 0 }));
}

/** Eligible articles on one drop — the drop histogram's bar height. */
export function dropCount(dropRows: DropFacetRow[], dropId: number, eligible: Set<number> | null): number {
  const articles = new Set<number>();
  for (const row of dropRows) {
    if (row.dropId !== dropId) continue;
    if (eligible && !eligible.has(row.articleId)) continue;
    articles.add(row.articleId);
  }
  return articles.size;
}
